import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';

import type { VehicleProfile, VehicleTemplate, VolumeUnit, WeightUnit } from '@klareospace/pharos-lib-types';
import { VEHICLE_PROFILES } from '@klareospace/pharos-lib-types';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';

import { createVehicleTemplateSchema } from '../schemas/vehicle-template.schema';
import type { VehicleTemplateFormInput } from '../schemas/vehicle-template.schema';
import { useCreateVehicleTemplate } from './use-create-vehicle-template';
import { useUpdateVehicleTemplate } from './use-update-vehicle-template';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseVehicleTemplateFormReturn {
  form: UseFormReturn<VehicleTemplateFormInput>;
  isPending: boolean;
  onSubmit: (data: VehicleTemplateFormInput) => Promise<void>;
}

export interface VehicleTemplateFormCallbacks {
  /** Called with the created template (create mode only), before `onSuccess`. */
  onCreated?: (template: VehicleTemplate) => void;
  onSuccess: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_MAX_VOLUME_UNIT: VolumeUnit = 'm3';
const DEFAULT_MAX_WEIGHT_UNIT: WeightUnit = 'kg';
const DEFAULT_PROFILE: VehicleProfile = VEHICLE_PROFILES.PEDESTRIAN;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function toKebabCase(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function getFormDefaults(vehicleTemplate: VehicleTemplate | null): VehicleTemplateFormInput {
  if (vehicleTemplate) {
    return {
      code: vehicleTemplate.code,
      maxOrderCapacity: String(vehicleTemplate.maxOrderCapacity),
      maxVolumeUnit: vehicleTemplate.maxVolumeCapacity.unit,
      maxVolumeValue: String(vehicleTemplate.maxVolumeCapacity.value),
      maxWeightUnit: vehicleTemplate.maxWeightCapacity.unit,
      maxWeightValue: String(vehicleTemplate.maxWeightCapacity.value),
      name: vehicleTemplate.name,
      profile: vehicleTemplate.profile,
    };
  }
  return {
    code: '',
    maxOrderCapacity: '',
    maxVolumeUnit: DEFAULT_MAX_VOLUME_UNIT,
    maxVolumeValue: '',
    maxWeightUnit: DEFAULT_MAX_WEIGHT_UNIT,
    maxWeightValue: '',
    name: '',
    profile: DEFAULT_PROFILE,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useVehicleTemplateForm(
  vehicleTemplate: VehicleTemplate | null,
  callbacks: VehicleTemplateFormCallbacks,
): UseVehicleTemplateFormReturn {
  const { onCreated, onSuccess } = callbacks;
  const { t } = useTranslation('settings');
  const isEditMode = vehicleTemplate !== null;

  const createVehicleTemplate = useCreateVehicleTemplate();
  const updateVehicleTemplate = useUpdateVehicleTemplate();

  const schema = useMemo(
    () =>
      createVehicleTemplateSchema(isEditMode, {
        codeFormat: t('vehicleTemplates.form.validation.codeFormat'),
        codeRequired: t('vehicleTemplates.form.validation.codeRequired'),
        labelRequired: t('vehicleTemplates.form.validation.labelRequired'),
        maxVolumeRequired: t('vehicleTemplates.form.validation.maxVolumeRequired'),
        maxWeightRequired: t('vehicleTemplates.form.validation.maxWeightRequired'),
        nonNegative: t('vehicleTemplates.form.validation.nonNegative'),
        positiveInteger: t('vehicleTemplates.form.validation.positiveInteger'),
      }),
    [isEditMode, t],
  );

  const form = useForm<VehicleTemplateFormInput>({
    defaultValues: getFormDefaults(vehicleTemplate),
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    form.reset(getFormDefaults(vehicleTemplate));
  }, [vehicleTemplate, form]);

  async function onSubmit(data: VehicleTemplateFormInput): Promise<void> {
    if (createVehicleTemplate.isPending || updateVehicleTemplate.isPending) return;

    const result = await submitTemplate({
      createVehicleTemplate,
      data,
      isEditMode,
      updateVehicleTemplate,
      vehicleTemplateId: vehicleTemplate?.id,
    });

    if ('error' in result) {
      handleSubmitError(result.error, { form, isEditMode, t });
      return;
    }

    form.reset();
    if (result.created) onCreated?.(result.created);
    onSuccess();
  }

  return {
    form,
    isPending: createVehicleTemplate.isPending || updateVehicleTemplate.isPending,
    onSubmit,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type SubmitTemplateContext = {
  createVehicleTemplate: ReturnType<typeof useCreateVehicleTemplate>;
  data: VehicleTemplateFormInput;
  isEditMode: boolean;
  updateVehicleTemplate: ReturnType<typeof useUpdateVehicleTemplate>;
  vehicleTemplateId: string | undefined;
};

type SubmitTemplateResult = { created: VehicleTemplate | null } | { error: unknown };

/**
 * Submits the create or update mutation. Never throws — mutation failures are caught and returned
 * as `{ error }` so the caller stays const-only (no `let` needed to escape a try/catch).
 * `created` is the new template in create mode, `null` in edit mode.
 */
async function submitTemplate(ctx: SubmitTemplateContext): Promise<SubmitTemplateResult> {
  const finalCode = ctx.data.code.trim();
  const maxVolumeCapacity = { unit: ctx.data.maxVolumeUnit, value: parseFloat(ctx.data.maxVolumeValue) };
  const maxWeightCapacity = { unit: ctx.data.maxWeightUnit, value: parseFloat(ctx.data.maxWeightValue) };
  const maxOrderCapacity = ctx.data.maxOrderCapacity ? parseFloat(ctx.data.maxOrderCapacity) : undefined;

  try {
    if (ctx.isEditMode && ctx.vehicleTemplateId) {
      await ctx.updateVehicleTemplate.mutateAsync({
        data: {
          code: finalCode,
          ...(maxOrderCapacity !== undefined && { maxOrderCapacity }),
          maxVolumeCapacity,
          maxWeightCapacity,
          name: ctx.data.name,
          profile: ctx.data.profile,
        },
        id: ctx.vehicleTemplateId,
      });
      return { created: null };
    }

    const created = await ctx.createVehicleTemplate.mutateAsync({
      code: finalCode,
      ...(maxOrderCapacity !== undefined && { maxOrderCapacity }),
      maxVolumeCapacity,
      maxWeightCapacity,
      name: ctx.data.name,
      profile: ctx.data.profile,
    });
    return { created };
  } catch (error: unknown) {
    return { error };
  }
}

type SubmitErrorContext = {
  form: UseFormReturn<VehicleTemplateFormInput>;
  isEditMode: boolean;
  t: TFunction;
};

function handleSubmitError(error: unknown, ctx: SubmitErrorContext): void {
  const detail = getApiErrorDetail(error);

  if (detail.errorCode === 'Conflict.DuplicateVehicleTemplateCode') {
    ctx.form.setError('code', { message: ctx.t('vehicleTemplates.drawer.validation.codeDuplicate') });
    return;
  }

  if (detail.errorCode === 'Conflict.DuplicateVehicleTemplateName') {
    ctx.form.setError('name', { message: ctx.t('vehicleTemplates.drawer.validation.nameDuplicate') });
    return;
  }

  const fallbackKey = ctx.isEditMode ? 'vehicleTemplates.toast.updateError' : 'vehicleTemplates.toast.createError';
  toast.error(detail.message || ctx.t(fallbackKey));
}
